import CRMContactView from "./CRMContact";
import { useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { crm } from "../../store/cabinetApi/crm";
import { PAGE_URL } from "../../constants";
import { selectDenormalizedData } from "../../store/crm/selectors";
import {
  ContactDenorm, ContactOrgDenorm, Leader, LeaderRelationshipDenorm, RootState, ThunkDispatchType
} from "../../store";
import { ORGANIZATION_PERMISSION } from "../../store/organization/types";
import { Contact, ContactRequest, PatchedContact, ContactNote } from "../../store/cabinetApi/generated/crm";
import { skipToken } from "@reduxjs/toolkit/query";
import { useDispatch, useSelector } from "react-redux";
import { fetchLeaders } from "../../store/leaders/actions";
import { useMountEffect } from "../../utils/hooks";
import { createSearchParams, useSearchParams } from "react-router";
import { uniqBy } from "lodash-es";
import { DateTime } from "luxon";
import { sendMessage } from "../../store/globalMessages/actions";
import { parseValidationErrors } from "../../utils/apiUtils";

// Type that allows File objects for avatar uploads
type ContactWithAvatarFile = Omit<ContactRequest, 'avatar'> & {
  avatar?: string | null | File;
};

type RouteParams = { contactId: string; };

const CRMContactContainer = () => {
  const params = useParams<RouteParams>();
  const [searchParams] = useSearchParams();
  const nowTime = useRef((new Date()).toISOString());

  const contactId = params.contactId ? Number(params.contactId) : undefined;
  const relationshipId = searchParams.get('relationship') ? Number(searchParams.get('relationship')) : undefined;
  const [orgString, setOrgString] = useState('');

  const userLicenseId = useSelector((root: RootState) => root.auth.user?.active_license.id);
  const user = useSelector((root: RootState) => root.auth.user);
  const leaders = useSelector((root: RootState) => root.leaders.leaders);
  
  const isAdmin = user?.active_license.org_permissions_groups.includes(ORGANIZATION_PERMISSION.ADMIN) ?? false;

  const { data: contactData, isLoading: loadingContact } = crm.useAppCrmContactsRetrieveQuery(contactId ?? skipToken);
  const { data: org } = crm.useAppCrmCompaniesRetrieveQuery(contactData?.company ?? skipToken);
  const { data: orgList } = crm.useAppCrmCompaniesListQuery({ 
    limit: 20, nameIcontains: orgString, orderBy: ["name"], hiddenDateIsnull: true
  });
  const { data: tagData } = crm.useAppCrmContactTagsListQuery({ limit: 1000 });
  const { data: relationshipTagData } = crm.useAppCrmRelationshipTagsListQuery({ limit: 1000 });
  const { data: typeData } = crm.useAppCrmContactTypesListQuery({ limit: 1000 });
  const { data: fieldTemplatesData } = crm.useAppCrmContactFieldTemplatesListQuery({ limit: 1000 });
  
  // Fetch specific relationship if relationshipId is provided via query param
  const { data: relationshipData } = crm.useAppCrmLeaderRelationshipsRetrieveQuery(
    relationshipId ?? skipToken
  );

  const { data: taskData } = crm.useAppCrmContactTasksListQuery(
    (contactId != null && (relationshipId == null || (relationshipId != null && relationshipData?.leader?.id)))
      ? {
        limit: 10,
        contacts: [contactId],
        teammates: relationshipData?.leader?.id ? [relationshipData?.leader?.id] : undefined
      }
      : skipToken
  );
  const { data: notesData } = crm.useAppCrmContactNotesListQuery(
    (contactId != null && (relationshipId == null || (relationshipId != null && relationshipData?.leader?.id)))
      ? {
        limit: 10,
        contacts: [contactId],
        teammates: relationshipData?.leader?.id ? [relationshipData?.leader?.id] : undefined
      }
      : skipToken
  );
  
  const { data: eventsData, isLoading: isEventsLoading } = crm.useAppCrmContactEventsListQuery(
    (contactId != null && (relationshipId == null || (relationshipId != null && relationshipData?.leader?.id)))
      ? { 
        limit: 20, 
        attendees: [contactId], 
        leaders: relationshipData?.leader?.id ? [relationshipData?.leader?.id] : undefined,
        startDateLte: nowTime.current,
        orderBy: ["-start_date"], 
        distinct: 'event_unique_id' 
      }
      : skipToken
  );
  
  // Still need to fetch all relationships for the relationshipLeaders list
  const { data: relationshipsListData } = crm.useAppCrmLeaderRelationshipsListQuery(contactId != null
    ? { limit: 50, offset: 0, contact: contactId }
    : skipToken
  );
  
  const { data: crmPreferencesData } = crm.useAppCrmPreferencesListQuery();

  // Get the specific relationship if it exists
  const relationship = relationshipData 
    ? selectDenormalizedData({ data: relationshipData }) as LeaderRelationshipDenorm | undefined
    : undefined;

  const relationships = relationshipsListData 
    && selectDenormalizedData(relationshipsListData) as LeaderRelationshipDenorm[];

  const relationshipLeaders = useMemo(() => (
    relationships?.map(r => r.leader).filter((r): r is Leader => !!r) || []
  ), [relationships]);

  const leaderOptions = uniqBy([...leaders, ...relationshipLeaders], l => l.id);

  const [updateContact] = crm.useAppCrmContactsPartialUpdateMutation();
  const [addNote] = crm.useAppCrmContactNotesCreateMutation();
  const [updateNote] = crm.useAppCrmContactNotesPartialUpdateMutation();
  const [deleteNote] = crm.useAppCrmContactNotesDestroyMutation();
  const [addTask] = crm.useAppCrmContactTasksCreateMutation();
  const [updateTask] = crm.useAppCrmContactTasksPartialUpdateMutation();
  const [deleteTask] = crm.useAppCrmContactTasksDestroyMutation();
  const [updateRelationship] = crm.useAppCrmLeaderRelationshipsPartialUpdateMutation();
  const [createRelationship] = crm.useAppCrmLeaderRelationshipsCreateMutation();
  const [deleteRelationship] = crm.useAppCrmLeaderRelationshipsDestroyMutation();
  const [updateCrmPreferences] = crm.useAppCrmPreferencesPartialUpdateMutation();
  const [mergeContacts] = crm.useAppCrmContactsMergeCreateMutation();
  const [fetchContactsByEmail] = crm.useLazyAppCrmContactsListQuery();

  // State for merge dialog
  const [existingContact, setExistingContact] = useState<Contact | null>(null);
  const [isMerging, setIsMerging] = useState(false);


  const typeList = typeData?.data || [];
  const tagList = tagData?.data || [];
  const relationshipTagList = relationshipTagData?.data || [];

  const contactType = typeList.find(t => t.id === contactData?.contact_type);

  const contact = selectDenormalizedData(
    { data: contactData },
    {
      ...(org && { company: org }),
      ...(contactType && { contact_type: contactType }),
    }
  ) as ContactDenorm | undefined;

  const orgs = useMemo(() => {
    let allOrgs = org ? [org as ContactOrgDenorm] : [];
    if (orgList) {
      allOrgs = uniqBy(allOrgs.concat(selectDenormalizedData(orgList) as ContactOrgDenorm[]), o => o.id);
    }
    return allOrgs;
  }, [org, orgList]);

  const taskList = taskData?.data || [];
  const notesList = notesData?.data || [];
  const eventsList = [...(eventsData?.data || [])]
    .sort((a, b) => DateTime.fromISO(b.start_date).toMillis() - DateTime.fromISO(a.start_date).toMillis());

  const dispatch = useDispatch<ThunkDispatchType>();
  const navigate = useNavigate();

  const handleNavigateToContacts = () => (
    navigate(PAGE_URL.CRM_PEOPLE)
  );

  const handleNavigateToDataView = (category: string) => (
    navigate({
      pathname: PAGE_URL.CRM_SEARCH,
      search: createSearchParams({ entities: [category] }).toString(),
    })
  );

  const handleNavigateToSettings = () => (
    navigate(`${PAGE_URL.SETTINGS}/crm/org`)
  );

  const handleNavigateToAnalyticsSettings = () => (
    navigate(`${PAGE_URL.SETTINGS}/analytics/org`)
  );

  const contactOrgPage = contact?.company?.id ? `${PAGE_URL.CRM_COMPANIES}/${contact?.company?.id}` : undefined;

  // Helper function to extract duplicate email from error response
  const extractDuplicateEmail = (error: unknown): string | null => {
    if (typeof error === "object" && error 
      && "data" in error && error.data && typeof error.data === "object" 
      && "detail" in error.data && typeof error.data.detail === "object" && error.data.detail) {
      
      const detail = error.data.detail as Record<string, unknown>;
      
      // Check for emails, primary_email, or all_emails fields
      const emailFields = ['emails', 'primary_email', 'all_emails'];
      for (const field of emailFields) {
        const fieldValue = detail[field];
        if (fieldValue && typeof fieldValue === 'object' && fieldValue !== null) {
          const fieldObj = fieldValue as Record<string, unknown>;
          if ('duplicate_email' in fieldObj) {
            const duplicateEmail = fieldObj.duplicate_email;
            if (typeof duplicateEmail === 'string') {
              return duplicateEmail;
            }
          }
        }
      }
    }
    return null;
  };

  // Helper function to find contact by email
  const findContactByEmail = async (email: string): Promise<Contact | null> => {
    try {
      const result = await fetchContactsByEmail({
        limit: 1,
        allEmailsContains: [email],
      }).unwrap();
      if (result.data && result.data.length > 0) {
        const foundContact = result.data[0];
        // Make sure it's not the current contact
        if (foundContact.id !== contact?.id) {
          return foundContact;
        }
      }
    } catch (error) {
      // If search fails, return null
    }
    return null;
  };

  const handleUpdateContactBasicInfo = async (info: Partial<ContactWithAvatarFile>) => {
    if (contact?.id) {
      try {
        await updateContact({ id: contact.id, patchedContact: info as PatchedContact }).unwrap();
      } catch (error: unknown) {
        // Check if this is a duplicate email error
        const duplicateEmail = extractDuplicateEmail(error);
        
        if (duplicateEmail) {
          // Find the contact that has this email
          const foundContact = await findContactByEmail(duplicateEmail);
          
          if (foundContact) {
            setExistingContact(foundContact);
            setIsMerging(false);
            return;
          }
        }
        
        // Show other validation errors as normal
        const validationErrors = parseValidationErrors(error);
        if (validationErrors) {
          validationErrors.forEach(([field, message]) => {
            dispatch(sendMessage({
              timeout: 5000,
              message: message,
              autoDismiss: true,
              header: "",
              position: {
                vertical: "top",
                horizontal: "center"
              },
              active: true,
              severity: "error",
            }));
          });
        }
      }
    }
  };

  const handleMergeContacts = async () => {
    if (!contact?.id || !existingContact?.id) return;
    
    setIsMerging(true);
    
    try {
      // Determine which contact should be primary (keep the current one)
      const primaryContactId = contact.id;
      const secondaryContactId = existingContact.id;
      
      await mergeContacts({
        primary_contact_id: primaryContactId,
        secondary_contact_ids: [secondaryContactId],
      }).unwrap();
      
      // Close dialog and navigate to the merged contact
      setExistingContact(null);
      setIsMerging(false);
      
      // Refresh the page to show updated contact
      window.location.reload();
    } catch (error) {
      setIsMerging(false);
      dispatch(sendMessage({
        timeout: 5000,
        message: "Failed to merge contacts. Please try again.",
        autoDismiss: true,
        header: "",
        position: {
          vertical: "top",
          horizontal: "center"
        },
        active: true,
        severity: "error",
      }));
    }
  };

  const handleCloseMergeDialog = () => {
    setExistingContact(null);
    setIsMerging(false);
  };

  const handleAddNote = async (note: ContactNote) => {
    // Include the relationship leader in teammates if a relationship is selected
    const relationshipLeaderId = relationship?.leader?.id;
    const teammates = relationshipLeaderId ? [relationshipLeaderId] : [];

    // Extract only the fields needed for the mutation request
    return await addNote({
      body: note.body,
      contacts: note.contacts,
      teammates: teammates,
      company: note.company || [],
      allowed_users: note.allowed_users || [],
    });
  };

  const handleAddTask = async ({ title, due_date }: { title: string; due_date?: string | null }) => {
    if (!contact || !userLicenseId) return;

    // Include the relationship leader in teammates if a relationship is selected
    const relationshipLeaderId = relationship?.leader?.id;
    const teammates = relationshipLeaderId ? [relationshipLeaderId] : [];

    await addTask({
      title: title,
      due_date: due_date,
      contacts: [contact.id],
      assignee: userLicenseId,
      teammates: teammates,
      company: [],
      allowed_users: [],
    });
  };

  const handleUpdateRelationships = async (leaderIds: Leader['id'][]) => {
    if (!contactId) return;

    // const existingRelationships = leaderIds.map(lId => relationships?.find(r => r.leader?.id === lId))
    //   .filter((r): r is LeaderRelationshipDenorm => !!r);
    const relationshipsToRemove = (relationships || []).filter(r => !leaderIds.find(lId => lId === r.leader?.id));
    const newRelationshipLeaderIds = leaderIds.filter(lId => !relationshipLeaders.find(l => l.id === lId));

    const removePromises = relationshipsToRemove.map(r => deleteRelationship(r.id));
    const addPromises = newRelationshipLeaderIds.map(lId => createRelationship({
      leader_id: lId, contact_id: contactId,
    }));

    await Promise.all(removePromises);
    await Promise.all(addPromises);
  };

  useMountEffect(() => {
    dispatch(fetchLeaders);
  });

  return (
    <CRMContactView
      contact={contact || null}
      onNavigateToContacts={handleNavigateToContacts}
      onNavigateToDataView={handleNavigateToDataView}
      onNavigateToSettings={handleNavigateToSettings}
      onNavigateToAnalyticsSettings={handleNavigateToAnalyticsSettings}
      isAdmin={isAdmin}
      onSaveBasicInfo={handleUpdateContactBasicInfo}
      orgOptions={orgs || []}
      typeOptions={typeList}
      tagOptions={tagList}
      fieldTemplates={fieldTemplatesData?.data || []}
      tasks={taskList}
      notes={notesList}
      events={eventsList}
      onAddNote={handleAddNote}
      onUpdateNote={updateNote}
      onDeleteNote={deleteNote}
      onAddTask={handleAddTask}
      onUpdateTask={updateTask}
      onDeleteTask={deleteTask}
      loading={loadingContact}
      loadingEvents={isEventsLoading}
      onSetOrgString={setOrgString}
      relationshipLeaders={relationshipLeaders}
      leaderOptions={leaderOptions}
      onUpdateRelationships={handleUpdateRelationships}
      contactOrgPage={contactOrgPage}
      crmPreferencesData={crmPreferencesData}
      onUpdateCrmPreferences={updateCrmPreferences}
      relationship={relationship}
      onUpdateRelationship={updateRelationship}
      relationshipTagOptions={relationshipTagList}
      mergeDialogState={{
        existingContact,
        isMerging,
      }}
      onMergeContacts={handleMergeContacts}
      onCloseMergeDialog={handleCloseMergeDialog}
    />
  );
};

export default CRMContactContainer;
